import { routes } from '@/config/routes';

import { NOTIFICATION_GROUP } from './constants';
import type { Notification } from './schemas';

/**
 * Where clicking a notification goes — US-101849 § NAVIGATION, one case per row of that table.
 *
 * Each destination needs an id the notification may not carry (an alert for a device that has
 * since been removed, a grouped subscription notification that names no single device). Rather
 * than build a dead link, every branch falls back to the listing that *does* exist, which is
 * also what the story implies for the grouped and pending-device cases.
 */
export function notificationDestination(notification: Notification): string {
  const { group, deviceId, referenceId } = notification;

  switch (group) {
    // "the device's Device Details page" — commands, alerts (incl. Disconnected) and maintenance.
    case NOTIFICATION_GROUP.COMMAND:
    case NOTIFICATION_GROUP.ALERT:
    case NOTIFICATION_GROUP.MAINTENANCE:
      return deviceId ? routes.device(deviceId) : routes.devices;

    // "the Device Listing screen" — lifecycle events fire for Pending QC / Pending Configuration
    // devices, which have no detail page to land on.
    case NOTIFICATION_GROUP.LIFECYCLE:
      return routes.devices;

    // "the subscription page" — single-device and grouped alike.
    case NOTIFICATION_GROUP.SUBSCRIPTION:
      return routes.subscriptions;

    // "that ticket/claim's detail page".
    case NOTIFICATION_GROUP.SUPPORT:
      return referenceId ? routes.ticket(referenceId) : routes.support;
    case NOTIFICATION_GROUP.WARRANTY:
      return referenceId ? routes.warrantyClaim(referenceId) : routes.warrantyClaims;

    // A replacement request is *not* a warranty claim, but no replacement route exists yet, so it
    // borrows the claim destination. Point this at the replacement detail page once one ships.
    case NOTIFICATION_GROUP.REPLACEMENT:
      return referenceId ? routes.warrantyClaim(referenceId) : routes.warrantyClaims;
  }
}
